import { useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { getIDFromUrl } from '@rossum/api-client';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import { IconSearch } from '@rossum/ui/icons/tabler';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { GridRowId, GridRowSelectionModel } from '@rossum/ui/x-data-grid-pro';
import { notify } from '../../toaster';
import { useCreateCustomRelation } from '../hooks/useCreateCustomRelation';
import { useCustomRelationsConfig } from '../hooks/useCustomRelationsConfig';
import { useFetchAnnotationCustomRelations } from '../hooks/useFetchAnnotationCustomRelations';
import { usePatchCustomRelationAnnotations } from '../hooks/usePatchCustomRelationAnnotations';
import { RelationCandidate } from '../hooks/useSearchRelationCandidates';
import { CreateRelationGrid } from './CreateRelationGrid';
import { SelectedAnnotationsFooter } from './SelectedAnnotationsFooter';

const DIALOG_WIDTH = 950;
const SEARCH_MIN_CHAR_LENGTH = 3;

const emptySelectionModel: GridRowSelectionModel = {
  type: 'include',
  ids: new Set(),
};

type CreateRelationDialogProps = {
  onClose: () => void;
  // Becomes the parent of the relation.
  rootAnnotationUrl: string | null;
  lockedSubtypeName?: string | null;
  // Recent documents from this queue are listed before the user searches.
  defaultQueueUrl?: string | null;
};

export const CreateRelationDialog = ({
  onClose,
  rootAnnotationUrl,
  lockedSubtypeName = null,
  defaultQueueUrl = null,
}: CreateRelationDialogProps) => {
  const intl = useIntl();

  const { allowedSubtypes } = useCustomRelationsConfig();

  const defaultSubtypeName =
    lockedSubtypeName ?? allowedSubtypes[0]?.name ?? null;

  const [subtypeName, setSubtypeName] = useState<string | null>(
    defaultSubtypeName
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedAnnotations, setSelectedAnnotations] = useState<
    RelationCandidate[]
  >([]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(emptySelectionModel);

  const resetSelection = () => {
    setSelectedAnnotations([]);
    setRowSelectionModel(emptySelectionModel);
  };

  const closeDialog = () => {
    setSubtypeName(defaultSubtypeName);
    setSearchValue('');
    resetSelection();
    onClose();
  };

  // The parent can only have one relation per sub-type (BE constraint),
  // so selected documents are appended to an existing one.
  const { customRelations } = useFetchAnnotationCustomRelations(
    rootAnnotationUrl ? getIDFromUrl(rootAnnotationUrl) : null
  );

  const existingRelation =
    customRelations.find(
      relation =>
        relation.parent === rootAnnotationUrl && relation.name === subtypeName
    ) ?? null;

  const { mutate: createRelation, isLoading: isCreating } =
    useCreateCustomRelation();

  const { mutate: patchRelation, isLoading: isPatching } =
    usePatchCustomRelationAnnotations();

  const handleSubmit = () => {
    const annotations = selectedAnnotations.map(annotation => annotation.url);

    if (!annotations.length || !rootAnnotationUrl || !subtypeName) return;

    const onSuccess = () => {
      notify.default({
        title: intl.formatMessage(
          { id: 'features.customRelations.createDialog.success' },
          { count: annotations.length }
        ),
      });
      closeDialog();
    };

    if (existingRelation) {
      patchRelation(
        {
          relationId: existingRelation.id,
          annotations: R.unique([
            ...existingRelation.annotations,
            ...annotations,
          ]),
        },
        { onSuccess }
      );
    } else {
      createRelation(
        { name: subtypeName, parent: rootAnnotationUrl, annotations },
        { onSuccess }
      );
    }
  };

  const handleSubtypeChange = (name: string) => {
    setSubtypeName(name);
    resetSelection();
  };

  const handleRowSelectionModelChange = (
    candidates: RelationCandidate[],
    newModel: GridRowSelectionModel
  ) => {
    const newSelection = [...selectedAnnotations, ...candidates].filter(
      annotation => newModel.ids.has(annotation.id)
    );

    setRowSelectionModel(newModel);
    setSelectedAnnotations(R.uniqueBy(newSelection, a => a.id));
  };

  const handleRemoveSelection = (id: GridRowId) => {
    setRowSelectionModel(prev => {
      const ids = new Set(prev.ids);
      ids.delete(id);

      return { ...prev, ids };
    });
    setSelectedAnnotations(prev =>
      prev.filter(annotation => annotation.id !== id)
    );
  };

  const isSearchValid = searchValue.length >= SEARCH_MIN_CHAR_LENGTH;
  const isSubmitting = isCreating || isPatching;

  return (
    <Dialog
      open={!!rootAnnotationUrl}
      onClose={closeDialog}
      slotProps={{
        paper: {
          elevation: 2,
          sx: {
            width: DIALOG_WIDTH,
            maxWidth: 'unset',
            // Must be a definite height (not minHeight), otherwise percentage
            // heights inside DialogContent collapse and the grid renders at 0.
            // Slightly taller than the attachments modal to make up for the
            // subtype select row and the results caption.
            height: 'min(88vh, 680px)',
          },
        },
      }}
    >
      <DialogTitle
        title={intl.formatMessage({
          id: 'features.customRelations.createDialog.title',
        })}
        onClose={closeDialog}
        closeDataCy="create-relation-dialog-close-icon"
      />
      <DialogContent>
        <Stack pt={2} height={1} spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label={intl.formatMessage({
                id: 'features.customRelations.createDialog.subtypeLabel',
              })}
              value={subtypeName ?? ''}
              onChange={event => handleSubtypeChange(event.target.value)}
              disabled={!!lockedSubtypeName}
              data-cy="create-relation-dialog-subtype-select"
              sx={{ width: 250 }}
            >
              {allowedSubtypes.map(subtype => (
                <MenuItem key={subtype.name} value={subtype.name}>
                  {subtype.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              placeholder={intl.formatMessage({
                id: 'features.customRelations.createDialog.searchPlaceholder',
              })}
              name="relationCandidateSearch"
              value={searchValue}
              disabled={!subtypeName}
              onChange={event => setSearchValue(event.target.value)}
              data-cy="create-relation-dialog-search"
              slotProps={{
                input: {
                  startAdornment: (
                    <SvgIcon fontSize="large">
                      <IconSearch />
                    </SvgIcon>
                  ),
                },
              }}
              sx={{ flex: 1 }}
              helperText={
                searchValue.length && !isSearchValid ? (
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage(
                      { id: 'containers.allDocuments.search.minLength' },
                      { minLength: SEARCH_MIN_CHAR_LENGTH }
                    )}
                  </Typography>
                ) : undefined
              }
            />
          </Stack>
          {rootAnnotationUrl && subtypeName ? (
            <CreateRelationGrid
              searchValue={isSearchValid ? searchValue : ''}
              subtypeName={subtypeName}
              rootAnnotationUrl={rootAnnotationUrl}
              excludedAnnotations={existingRelation?.annotations ?? []}
              defaultQueueUrl={defaultQueueUrl}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
            />
          ) : null}
          <SelectedAnnotationsFooter
            selectedAnnotations={selectedAnnotations.map(
              ({ id, original_file_name }) => ({
                name: original_file_name || '',
                id,
              })
            )}
            onRemove={handleRemoveSelection}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifySelf: 'right', p: 3, pt: 0 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={closeDialog}
          data-cy="create-relation-dialog-cancel-btn"
        >
          {intl.formatMessage({ id: 'components.modal.cancel' })}
        </Button>
        <Button
          variant="contained"
          startIcon={
            isSubmitting ? <CircularProgress color="inherit" size={16} /> : null
          }
          disabled={!selectedAnnotations.length || !subtypeName || isSubmitting}
          onClick={handleSubmit}
          data-cy="create-relation-dialog-submit-btn"
        >
          {intl.formatMessage(
            {
              id: existingRelation
                ? ('features.customRelations.createDialog.submitAdd' as const)
                : ('features.customRelations.createDialog.submitCreate' as const),
            },
            { count: selectedAnnotations.length }
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
